/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { useStore } from 'effector-react';
import classNames from 'classnames';
import { type AutoRotateItem, ImageType } from '@types';
import { PUBLIC_DOMAIN } from '@constants';
import { $loadedScene } from '@stores/loadedStore';
import {
  $GateRotateItemEditing,
  $rotateCurrentLeft,
  $rotateEditAction,
  $rotateIsPlaying,
  type RotateEditAction,
} from '@stores/autoRotateStores';
import Image from '@components/Image';
import { TipWrapper } from '@components/TipWrapper';
import useToggle from '@hooks/useToggle';
import { MainPlayer } from '@stores/krpPlayerStores';
import { PX_PER_SEC } from '../constants';
import { onRotateItemClick } from '../actions/onRotateItemClick';
import { insertRotateItem } from '../actions/krpRotate';
import { rotateTo } from '../utils/rotateLookTo';

interface ItemProps {
  item: AutoRotateItem;
  index: number;
  selectedIndex: number;
  prevId?: string;
  isOn: boolean;
  onAction: (payload: {
    actionType: RotateEditAction['type'];
    index: number;
    left: number;
  }) => void;
}

function RotateListItem({
  item,
  index,
  selectedIndex,
  prevId,
  isOn,
  onAction,
}: ItemProps) {
  const curAction = useStore($rotateEditAction);
  const hovered = useToggle(false);
  const isPlaying = useStore($rotateIsPlaying);
  const line = useToggle(false);

  useEffect(() => {
    if (!isPlaying || !isOn) return;
    const curTime = $rotateCurrentLeft.getState() / PX_PER_SEC;
    const duration = item.duration - (curTime - item.startAt);
    rotateTo({ ...item, duration });
  }, [isPlaying, isOn]);

  return (
    <div
      key={index}
      onMouseEnter={() => hovered.on()}
      onMouseLeave={() => hovered.off()}
      className={classNames(
        'absolute top-0 bottom-0 flex items-center justify-center rounded text-sm',
        {
          'hover:z-40 hover:ring-2 hover:ring-white': !curAction,
          'z-40 ring-2 ring-white':
            curAction?.index === index && curAction?.type === 'moveBlock',
        }
      )}
      style={{
        left: item.startAt * PX_PER_SEC,
        width: item.duration * PX_PER_SEC,
        backgroundColor: '#45577C',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0"
        onMouseDown={(e) => {
          if (index === 0) {
            e.stopPropagation();
            return;
          }
          onAction({
            actionType: 'moveBlock',
            index,
            left: item.startAt * PX_PER_SEC,
          });
        }}
      />
      <div
        className={classNames(
          'absolute left-0 flex items-center justify-center rounded-r',
          {
            'bg-orange': index - 1 === selectedIndex,
            'cursor-pointer bg-white bg-opacity-20 hover:bg-opacity-100 hover:text-black':
              index - 1 !== selectedIndex,
          }
        )}
        style={{ width: 19, height: 26 }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (prevId) {
            onRotateItemClick(prevId);
          }
        }}
      >
        {index}
      </div>
      {index !== 0 && (
        <div
          className={classNames('absolute left-0 h-40 w-4 cursor-ew-resize', {
            'hover:bg-orange': !curAction,
            'bg-orange':
              curAction?.index === index && curAction?.type === 'resizeLeft',
          })}
          onMouseDown={() => {
            onAction({
              actionType: 'resizeLeft',
              index,
              left: item.startAt * PX_PER_SEC,
            });
          }}
        />
      )}
      {`${Math.round(item.duration * 100) / 100}s`}
      <div
        className={classNames(
          'absolute right-0 flex items-center justify-center rounded-l',
          {
            'bg-orange': index === selectedIndex,
            'cursor-pointer bg-white bg-opacity-20 hover:bg-opacity-100 hover:text-black':
              index !== selectedIndex,
          }
        )}
        style={{ width: 19, height: 26 }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onRotateItemClick(item.id);
        }}
      >
        {index + 1}
      </div>
      <div
        className={classNames('absolute right-0 h-40 w-4 cursor-ew-resize', {
          'hover:bg-orange': !curAction,
          'bg-orange':
            curAction?.index === index && curAction?.type === 'resizeRight',
        })}
        onMouseDown={() => {
          onAction({
            actionType: 'resizeRight',
            index,
            left: (item.startAt + item.duration) * PX_PER_SEC,
          });
        }}
      />
      {hovered.isOn && line.isOn && (
        <div className="absolute bottom-0 top-0 w-2 bg-white bg-opacity-50" />
      )}
      {hovered.isOn && (
        <TipWrapper
          text="插入节点"
          placement="bottom"
          offsetVal={0}
          className="absolute bottom-0"
          size="sm"
        >
          <Image
            className="cursor-pointer"
            src="/imgs/insert.66bef3b8b5e4c1bddff768e284f513e9.png"
            type={ImageType.sysMedia}
            style={{ width: 16, height: 17 }}
            onClick={(e) => {
              e.stopPropagation();
              hovered.off();
              insertRotateItem({ index });
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onMouseEnter={() => line.on()}
            onMouseLeave={() => line.off()}
          />
        </TipWrapper>
      )}
    </div>
  );
}

interface Props {
  width: number;
  onAction: ItemProps['onAction'];
}

export function RorateList({ width, onAction }: Props) {
  const scene = useStore($loadedScene)!;
  const list = scene.config.autoRotate?.tourData ?? [];
  const selectedId = useStore($GateRotateItemEditing.state)?.id;
  const selectedIndex = list.findIndex((item) => item.id === selectedId);
  const curLeft = useStore($rotateCurrentLeft);
  const curTime = curLeft / PX_PER_SEC;
  const lastItem = list[list.length - 1];
  const right = lastItem
    ? (lastItem.startAt + lastItem.duration) * PX_PER_SEC
    : 0;

  return (
    <div
      className="relative mt-20 flex h-50 w-full items-center bg-black-#34"
      style={{ width }}
    >
      <div
        className="absolute left-0 h-50 rounded"
        style={{
          width: right,
          backgroundImage: `url(${PUBLIC_DOMAIN}/imgs/tl-bg.5375cdf782e9eefbffc5a64962bf8888.png)`,
        }}
      />
      {list.map((item, index) => (
        <RotateListItem
          key={item.id}
          item={item}
          index={index}
          selectedIndex={selectedIndex}
          prevId={list[index - 1]?.id}
          isOn={
            curTime >= item.startAt && curTime < item.startAt + item.duration
          }
          onAction={onAction}
        />
      ))}
      <div
        className="absolute left-0 z-50 flex cursor-pointer items-center justify-center rounded-r bg-white text-sm text-black"
        style={{ height: 26 }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          MainPlayer.get()?.lookTo({
            hlookat: scene.view.hLookAt,
            vlookat: scene.view.vLookAt,
            fov: scene.view.fov,
          });
        }}
      >
        起始
      </div>
    </div>
  );
}
